import type { ReactNode } from "react";
import { cn } from "../../lib/cn";

export interface CollapseProps {
  open: boolean;
  children: ReactNode;
  className?: string;
}

/**
 * Animates open/closed without knowing the content's height and without a dependency.
 *
 * `grid-template-rows: 0fr → 1fr` is the trick: a grid track can be transitioned between
 * fractional sizes, so the row grows to exactly its content height with no measurement, no
 * ResizeObserver, and no max-height guess that clips tall content or adds a lazy tail on short
 * content. `@radix-ui/react-collapsible` would do this too but is not installed, and adding a
 * package for one disclosure is not a trade worth making.
 *
 * The inner element needs `overflow-hidden` or the content spills out of the collapsing track.
 */
export function Collapse({ open, children, className }: CollapseProps) {
  return (
    <div
      className={cn(
        "grid transition-[grid-template-rows] duration-base ease-standard motion-reduce:transition-none",
        open ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
        className,
      )}
    >
      <div className={cn("overflow-hidden transition-opacity duration-fast", open ? "opacity-100" : "opacity-0")}>
        {children}
      </div>
    </div>
  );
}
